import * as React from 'react';

import { getAuthMode } from 'js/lib/authModeDeepLinkUtils';
import useRouter from 'js/lib/useRouter';
import user from 'js/lib/user';

import ErrorPage from 'bundles/coursera-ui/components/rich/ErrorPage';
import BypassLogin from 'bundles/enterprise-legacy-learner-home/components/BypassLogin';
import EnterpriseHomeAccessErrorPage from 'bundles/program-home/components/enterprise-home/EnterpriseHomeAccessErrorPage';
import ResendJwtInviteLink from 'bundles/saml-sso/components/ResendJwtInviteLink';
import { isForcedSSOLoginFlowEnabled } from 'bundles/saml-sso/epicFeatureFlags';
import { hasExpiredInviteLink } from 'bundles/saml-sso/utils/JWTLoginAPIUtils';
import ProgramPreJoinCheck from 'bundles/unified-common/components/enterprise/ProgramPreJoinCheckNonConfigurable';
import { useEnterpriseProductFeatures } from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import {
  useAcceptanceNoticeLogs,
  useProgram,
  useProgramMembership,
  useRefetchAcceptanceNoticeLogs,
  useRequirePreJoinCheck,
} from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';
import { useIsJitOrganization } from 'bundles/unified-common/hooks/useIsJitOrganization';
import useJwtInviteCheck from 'bundles/unified-common/hooks/useJwtInviteCheck';
import useSsoCheck from 'bundles/unified-common/hooks/useSsoCheck';
import { isWithinSsoGracePeriod } from 'bundles/unified-common/utils/ssoGracePeriod';

const ATTEMPT_SSO_LOGIN = 'attemptSSOLogin';

type Props = {
  children: React.ReactNode;
};

const EnterpriseUserStateCheckWrapper = ({ children }: Props) => {
  const router = useRouter();
  const enterpriseProductFeatures = useEnterpriseProductFeatures();
  const acceptanceNoticeLogs = useAcceptanceNoticeLogs() || [];
  const refetchAcceptanceNoticeLogs = useRefetchAcceptanceNoticeLogs();
  const program = useProgram();
  const programMembership = useProgramMembership();
  const requirePreJoinCheck = useRequirePreJoinCheck();
  const thirdPartyOrganization = useThirdPartyOrganization();

  const isAuthenticatedUser = user.isAuthenticatedUser();
  const userId = user.get()?.id;

  const { isProgramMember, isProgramAdmin } = programMembership;
  const shouldAttemptSsoLogin = router.location.query[ATTEMPT_SSO_LOGIN] === 'true';
  const isInLoginMode = getAuthMode(router.location.query, router.location.hash) === 'login';

  const { isJit, isLoading: isJitLoading } = useIsJitOrganization({
    orgId: thirdPartyOrganization?.id,
    skip: !shouldAttemptSsoLogin || !isAuthenticatedUser,
  });

  useJwtInviteCheck({
    router,
    programMembership: {
      isInvited: programMembership.isInvited,
      isEmailNotVerified: !!programMembership.isEmailNotVerified,
    },
    isProgramAdmin,
    isAuthenticatedUser,
  });
  useSsoCheck({
    router,
    userId,
    isInLoginMode,
    program,
    thirdPartyOrganization,
    isProgramAdmin,
    isProgramMember,
    isAuthenticatedUser,
  });

  const showResendInviteLink = hasExpiredInviteLink(router);

  // Wait for isJitLoading to complete before rendering
  if (shouldAttemptSsoLogin && isAuthenticatedUser && isJitLoading) {
    return null;
  }

  const dismissExpiredInviteLink = () => {
    router.replace({
      pathname: router.location.pathname,
      params: router.params,
      query: { ...router.location.query, organization: null, webToken: null, errorCode: null },
    });
  };

  const shouldRedirectToSso =
    shouldAttemptSsoLogin &&
    thirdPartyOrganization?.id &&
    (!isAuthenticatedUser || (isJit && !isWithinSsoGracePeriod(thirdPartyOrganization?.id ?? '')));

  let content;
  if (shouldRedirectToSso) {
    content = <BypassLogin thirdPartyOrganizationId={thirdPartyOrganization.id} />;
  } else if (
    enterpriseProductFeatures?.enableInvitedOnlyProgram &&
    (!isAuthenticatedUser ||
      (!programMembership?.isMembershipLoading &&
        !programMembership?.isProgramAdmin &&
        !programMembership?.isProgramMember &&
        !programMembership?.isInvited))
  ) {
    content = (
      // Need to disable the page header and footer to only render the ones in the
      // EnterpriseHeaderFooterWrapper, double-renders both elements otherwise
      <ErrorPage disableHeader disableFooter>
        <EnterpriseHomeAccessErrorPage
          isLoggedIn={isAuthenticatedUser}
          thirdPartyOrganizationId={thirdPartyOrganization?.id}
          programId={program?.id}
        />
      </ErrorPage>
    );
  } else if (
    program &&
    requirePreJoinCheck &&
    acceptanceNoticeLogs.length === 0 &&
    !program?.endedAt &&
    !isForcedSSOLoginFlowEnabled()
  ) {
    const programPreJoinCheckProps = {
      isAuthenticatedUser,
      programId: program.id,
      programSlug: program.slug,
      programName: program.name,
      program,
      router,
      thirdPartyOrganizationId: thirdPartyOrganization?.id,
      thirdPartyOrganization,
      userId,
      enableSkillsInSearchAndBrowse: enterpriseProductFeatures?.enableSkillsInSearchAndBrowse,
      reloadProgramMembership: () => {
        void refetchAcceptanceNoticeLogs?.();
      },
    };
    content = <ProgramPreJoinCheck {...programPreJoinCheckProps} />;
  } else {
    content = <React.Fragment>{children}</React.Fragment>;
  }

  return (
    <React.Fragment>
      {content}
      {showResendInviteLink && <ResendJwtInviteLink onClose={dismissExpiredInviteLink} />}
    </React.Fragment>
  );
};

export default EnterpriseUserStateCheckWrapper;
